import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../firebaseconfig";

import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";

import { Mail, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * ForgotPassword
 * - Recuperación de contraseña con Firebase Auth (Email/Password)
 * - Incluye actionCodeSettings para redirigir a tu web después del reset
 * - Loguea errores completos en DEV para debug real
 */
export default function ForgotPassword({ loginPath = "/login" }) {
  // ✅ Cambiá esto por tu URL real
  // - Dev: http://localhost:5173  (o el puerto que uses)
  // - Prod: https://tudominio.com
  const APP_URL = import.meta?.env?.VITE_APP_URL || window.location.origin;

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const { user } = useAuth();

  const isDev = useMemo(() => {
    // Vite usa import.meta.env.DEV
    // CRA usa process.env.NODE_ENV
    return (import.meta?.env?.DEV ?? process.env.NODE_ENV !== "production") === true;
  }, []);

  const validateEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

  const mapFirebaseError = (code) => {
    switch (code) {
      case "auth/user-not-found":
        return "No existe un usuario con ese email.";
      case "auth/invalid-email":
        return "Email inválido.";
      case "auth/too-many-requests":
        return "Demasiados intentos. Probá más tarde.";
      case "auth/network-request-failed":
        return "Error de red. Revisá tu conexión.";
      case "auth/operation-not-allowed":
        return "El método Email/Password no está habilitado en Firebase Auth.";
      default:
        return "Ocurrió un error. Intentá nuevamente.";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const cleanEmail = String(email || "").trim();

    if (!cleanEmail) {
      setError("Ingresá tu email.");
      return;
    }
    if (!validateEmail(cleanEmail)) {
      setError("Formato de email inválido.");
      return;
    }

    setLoading(true);

    try {
      // ✅ Esto hace que el email de Firebase vuelva a tu app después de la acción.
      // Importante: tu dominio debe estar en Authorized domains en Firebase Auth.
      const actionCodeSettings = {
        url: `${APP_URL}${loginPath}`, // vuelve a /login (o donde quieras)
        handleCodeInApp: false,
      };

      await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);

      setSuccess(
        "Si el email existe, te enviamos un enlace para restablecer tu contraseña. Revisá Spam/Promociones."
      );

      if (isDev) {
        console.log("[ForgotPassword] Reset solicitado OK:", {
          email: cleanEmail,
          redirectUrl: actionCodeSettings.url,
          authProject: auth?.app?.options?.projectId,
          authDomain: auth?.app?.options?.authDomain,
        });
      }
    } catch (err) {
      const code = err?.code;
      const message = err?.message;

      setError(mapFirebaseError(code));

      // ✅ Debug real en consola (solo dev)
      if (isDev) {
        console.group("[ForgotPassword] sendPasswordResetEmail ERROR");
        console.log("code:", code);
        console.log("message:", message);
        console.log("full error:", err);
        console.log("authProject:", auth?.app?.options?.projectId);
        console.log("authDomain:", auth?.app?.options?.authDomain);
        console.log("APP_URL:", APP_URL);
        console.groupEnd();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <Link
            to={loginPath}
            className="inline-flex items-center text-green-700 hover:text-green-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a iniciar sesión
          </Link>
        </div>

        <Card className="shadow-xl border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-bold text-gray-900">
              Recuperar contraseña
            </CardTitle>
            <CardDescription className="text-gray-600">
              Ingresá tu email y te enviaremos un enlace para restablecer la contraseña.
            </CardDescription>

            {user?.email && (
              <p className="mt-2 text-sm text-gray-500">
                Estás logueado como{" "}
                <span className="font-medium text-gray-700">{user.email}</span>. Igual
                podés usar este formulario.
              </p>
            )}
          </CardHeader>

          <CardContent>
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              aria-describedby={error ? "fp-error" : success ? "fp-success" : undefined}
            >
              {success && (
                <p
                  id="fp-success"
                  role="status"
                  aria-live="polite"
                  className="text-sm text-green-600"
                >
                  {success}
                </p>
              )}

              {error && (
                <p
                  id="fp-error"
                  role="alert"
                  aria-live="assertive"
                  className="text-sm text-red-600"
                >
                  {error}
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  Correo electrónico
                </Label>

                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-gray-300 focus:border-green-500 focus:ring-green-500"
                    aria-invalid={!!error}
                    required
                  />
                </div>

                {/* Mini ayuda de debug para vos (no molesta al usuario) */}
                {isDev && (
                  <p className="text-xs text-gray-400">
                    DEV: projectId={String(auth?.app?.options?.projectId || "-")} · authDomain=
                    {String(auth?.app?.options?.authDomain || "-")}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full text-white font-medium py-2.5 transition-colors disabled:opacity-60"
                style={{ backgroundColor: "#003226" }}
                disabled={loading || !validateEmail(email)}
                aria-busy={loading}
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-white animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>
                    Enviando...
                  </span>
                ) : (
                  "Enviar link de recuperación"
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="text-center pt-4">
            <p className="text-sm text-gray-600">
              <Link to={loginPath} className="text-green-600 hover:text-green-700 font-medium">
                Volver a iniciar sesión
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}